import React, {
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  createTheme,
  CssBaseline,
  Theme,
  ThemeOptions,
  ThemeProvider,
} from "@mui/material";
import {
  _throw,
  IllegalStateError,
  NullError,
} from "shared-ts-utils-core";
import { AppThemeUtils } from "./AppThemeUtils";
import { useThemeUserSettingWithDefault } from "./useThemeUserSettingWithDefault";
import { checkThemeName, ConcreteThemeName, ThemeName } from "./ThemeName";

type AppThemeContextType = {
  themeName: ThemeName;
  themeOptions: ThemeOptions;
  setTheme: React.Dispatch<SetStateAction<ThemeName>>;
  setThemeOptions: (themeName: ThemeName, newOptions: ThemeOptions) => void;
};

export const AppThemeContext = React.createContext<AppThemeContextType>(
  undefined as unknown as AppThemeContextType
);

export interface AppThemeProviderProps {
  initialTheme?: string;
  themes: Record<ConcreteThemeName, ThemeOptions>;
  children?: ReactNode | ((theme: Theme) => ReactNode);
}

export const AppThemeProvider: FC<AppThemeProviderProps> = (props) => {
  const initialThemeName = checkThemeName(props.initialTheme) ?? "auto";

  const [themeName, setThemeName] =
    useThemeUserSettingWithDefault(initialThemeName);

  const concreteThemeName = resolveConcreteThemeName(themeName as ThemeName);

  const [themeOptions, setThemeOptions] = useState<
    Record<string, ThemeOptions>
  >(props.themes);

  const [themes, setThemes] = useState<Record<ConcreteThemeName, Theme>>(
    AppThemeUtils.createThemes(props.themes)
  );

  const currentTheme =
    themes[concreteThemeName] ??
    _throw(new NullError(`themes[${concreteThemeName}]`));
  const currentThemeOptions =
    themeOptions[concreteThemeName] ??
    _throw(new NullError(`themeOptions[${concreteThemeName}]`));

  useEffect(() => {
    setThemeOptions(props.themes);
    setThemes(AppThemeUtils.createThemes(props.themes));
  }, [props.themes]);

  const handleSetThemeOptions = (
    themeName: ThemeName,
    options: ThemeOptions
  ) => {
    const exising = themes[concreteThemeName];
    if (exising == null) {
      throw new IllegalStateError("Theme does not exist: " + concreteThemeName);
    }
    const newTheme = createTheme(exising, options);
    setThemes((prevState) => {
      return {
        ...prevState,
        [themeName]: newTheme,
      };
    });
  };

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline enableColorScheme={true} />

      <AppThemeContext.Provider
        value={{
          themeName: themeName,
          themeOptions: currentThemeOptions,
          setTheme: setThemeName,
          setThemeOptions: handleSetThemeOptions,
        }}
      >
        {typeof props.children === "function"
          ? props.children(currentTheme)
          : props.children}
      </AppThemeContext.Provider>
    </ThemeProvider>
  );
};

/*export const useSetAppTheme = (): React.Dispatch<SetStateAction<ThemeName>> => {
  const context = useContext(AppThemeContext);

  if (context === undefined) {
    throw new Error("useSetAppTheme must be used within a AppThemeProvider");
  }
  return context.setTheme;
};*/

export const useAppTheme = (): AppThemeContextType => {
  const context = useContext(AppThemeContext);
  if (context === undefined) {
    throw new Error("useAppTheme must be used within a AppThemeProvider");
  }
  return context;
};

const resolveConcreteThemeName = (
  initialTheme: ThemeName
): ConcreteThemeName => {
  if (initialTheme === "auto") {
    return resolveSystemTheme();
  }
  return initialTheme;
};

const resolveSystemTheme = (): ConcreteThemeName => {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};
